import Modal from "@/components/Modal/Modal";
import { useEffect, useState } from "react"
import { useForm } from "react-hook-form";
import { useAppDispatch } from "@/store/hooks";
import { getInactiveUsers, getManagers, getMyTeamGCDetail } from "@/store/slices/teamSlice";
import { useSelector } from "react-redux";
import FormControl from "@/components/FormControl/FormControl";
import { excelFormat } from "@/lib/constants";
import dayjs from "dayjs";
import toast from "@/lib/toast";
import { saveAsExcelFile } from "@/services/excelService/excelSevice";
import team from "@/services/api/team";
import { dateFormatter } from "@/components/DateFormat/DateFormat";
import { getRoles } from "@/store/slices/roleSlice";
import type { RootState } from "@/store/rootReducer";
import { getDepartments, getDesignations } from "@/store/slices/organizationalUnitSlice";

interface AttendanceReportProps {
    openReportModal: boolean;
    setOpenReportModal: () => void;
}

type AttendanceFormValues = {
    users: any[];
    managers: any[];
    formatSelect: string;
    inActiveUserCheckBox?: boolean;
    inActiveUser?: any[];
    ToDate?: string | null;
    FromDate?: string | null;
    departments?: any[];
    designations?: any[];
    roles?: any[];
}

const AttendanceReport: React.FC<AttendanceReportProps> = ({ openReportModal, setOpenReportModal }) => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isEmailLoading, setIsEmailLoading] = useState<boolean>(false);
    const dispatch = useAppDispatch();

    const { teams, managers, inActiveUser } = useSelector(
        (state: any) => state.team
    );

    const {designation, department} =  useSelector((state: RootState) => state.organizationalUnit);
    const {roles} = useSelector((state: RootState) => state.role);

    const { control, watch, reset, handleSubmit } = useForm<AttendanceFormValues>({
        defaultValues: {
            users: [],
            managers: [],
            inActiveUser: [],
            formatSelect: "",
            inActiveUserCheckBox: false,
            FromDate: null,
            ToDate: null,
            departments: [],
            designations: [],
            roles: []
        },
    });
    const isInactiveChecked = watch("inActiveUserCheckBox");

    const onSubmit =
        (action: "download" | "email") =>
            async (formData: AttendanceFormValues) => {
                try {
                    const {
                        FromDate,
                        ToDate,
                        users,
                        managers,
                        formatSelect,
                        inActiveUserCheckBox,
                        inActiveUser,
                    } = formData;

                    const params = {
                        endDate: dateFormatter(ToDate, "MM-dd-yyyy"),
                        startDate: dateFormatter(FromDate, "MM-dd-yyyy"),
                        reportDate: dateFormatter(ToDate, "MM-dd-yyyy"),

                        isCustom: 1,
                        userId: 0,

                        userIds: (users || []).map(u => u.UserId).join(","),
                        managerIds: (managers || []).map(m => m.Id).join(","),

                        IsSendToMail: action === "email",
                        UserStatus: !inActiveUserCheckBox,

                        DeactiveUserIds: inActiveUserCheckBox
                            ? (inActiveUser || []).map(u => u.UserId).join(",")
                            : "",
                        IncludeAllUsers: false,
                        excelType: formatSelect || "format1",
                        departmentIds: (formData.departments || []).map(d => d.Id).join(","),
                        designationIds: (formData.designations || []).map(d => d.Id).join(","),
                        roleIds: (formData.roles || []).map(r => r.Id).join(","),
                    };

                    if (action === "download") {
                        setIsLoading(true);
                        const res = await team.exportAttendanceReport(params);
                        saveAsExcelFile(res, "AttendanceReport");
                        toast.success("File downloaded successfully");
                    }
                    if (action === "email") {
                        setIsEmailLoading(true);
                        await team.emailAttendanceReport(params);
                        toast.success("Email sent successfully");
                    }

                } catch (error) {
                    toast.error("Something went wrong");
                } finally {
                    setIsLoading(false);
                    setIsEmailLoading(false);
                    setOpenReportModal();
                }
            };

    const handleDownload = handleSubmit(onSubmit("download"));
    const handleSendEmail = handleSubmit(onSubmit("email"));

    useEffect(() => {
        if (teams?.Members?.length && managers?.length && inActiveUser?.length && department?.Items?.length && designation?.Items?.length && roles?.length) {
            reset({
                users: teams.Members,
                managers: managers,
                inActiveUser: inActiveUser,
                departments: department?.Items || [],
                designations: designation?.Items || [],
                roles: roles,
                formatSelect: excelFormat[0]!.value,
            });
        }

    }, [teams?.Members, managers, inActiveUser, department, designation, roles, reset, setOpenReportModal]);

    useEffect(() => {
        if (!openReportModal) return;
        dispatch(getMyTeamGCDetail({ from: 1, to: -1, text: "", Status: "all" }));
        dispatch(getInactiveUsers());
        dispatch(getManagers());
        dispatch(getRoles());
        dispatch(getDepartments({ from: 1, to: -1, text: "" }));
        dispatch(getDesignations({ from: 1, to: -1, text: "" }));
    }, [openReportModal, dispatch])
 
    
    return (
        <Modal
            modalOpen={openReportModal}
            closeModal={setOpenReportModal}
            heading="Attendance Report"
            saveButtonText="Download"
            isSendEmail={true}
            onSave={handleDownload}
            SendEmail={handleSendEmail}
            isLoading={isLoading}
            isEmailLoading={isEmailLoading}
        >
            <div className="grid grid-cols-2 gap-x-6 gap-y-10 items-start">

                <FormControl
                    control={control}
                    controlType="date"
                    label="From Date"
                    name="FromDate"
                    maxDate={dayjs()}
                />

                <FormControl
                    control={control}
                    controlType="date"
                    label="To Date"
                    name="ToDate"
                    maxDate={dayjs()}
                />

                <FormControl
                    control={control}
                    controlType="select"
                    name="users"
                    label="Select Users"
                    options={teams?.Members || []}
                    idKey="UserId"
                    nameKey="UserName"
                    isMulti
                    returnObject
                    isSelectAll={true}
                />

                <FormControl
                    control={control}
                    controlType="select"
                    name="managers"
                    label="Select Managers"
                    options={managers}
                    idKey="Id"
                    nameKey="Name"
                    isMulti
                    returnObject
                    isSelectAll={true}
                />

                <FormControl
                    control={control}
                    controlType="select"
                    name="departments"
                    label="Select Departments"
                    options={department?.Items || []}
                    idKey="Id"
                    nameKey="Name"
                    isMulti
                    returnObject
                    isSelectAll={true}
                />

                <FormControl
                    control={control}
                    controlType="select"
                    name="designations"
                    label="Select Designations"
                    options={designation?.Items || []}
                    idKey="Id"
                    nameKey="Name"
                    isMulti
                    returnObject
                    isSelectAll={true}

                />

                <FormControl
                    control={control}
                    controlType="select"
                    name="roles"
                    label="Select Roles"
                    options={roles}
                    idKey="Id"
                    nameKey="Name"
                    isMulti
                    returnObject
                    isSelectAll={true}
                />

                <FormControl
                    controlType="select"
                    name="formatSelect"
                    options={excelFormat}
                    enableSearch={false}
                    control={control}
                    label="Select Format"
                />

                <FormControl
                    controlType="checkbox"
                    name="inActiveUserCheckBox"
                    label="Inactive Users"
                    control={control}
                />
              

                {isInactiveChecked && (
                    <FormControl
                        control={control}
                        controlType="select"
                        name="inActiveUser"
                        label="Select Inactive Users"
                        options={inActiveUser}
                        idKey="UserId"
                        nameKey="UserName"
                        isMulti
                        returnObject
                        isSelectAll={true}
                    />
                )}
            </div>
        </Modal>
    )
}
export default AttendanceReport


